import { PropRuntimeGraph } from '../../../../../shared/types/PropRuntimeGraph';
import { RuntimePropInstance } from '../PropMechanicsTypes';
import { RuntimeNodeBase } from './RuntimeNodeBase';
import { isRuntimePropInstance, pickRandomItems } from './RuntimeNodeUtils';

/**
 * 条件筛选节点。
 *
 * 条件为真时输出上游目标，为假时输出空数组。
 */
export class TargetFilterNode extends RuntimeNodeBase {
    /**
     * 输出目标数组端口。
     *
     * @returns 条件成立时的上游目标集合。
     */
    public targets(): RuntimePropInstance[] {
        const targets = this.inputTargets('targets');
        if (!targets.length) return [];
        return this.inputCondition('condition', true) ? targets : [];
    }
}

/**
 * 品质筛选节点。
 *
 * 从上游目标中筛出指定品质的实例；多个品质输入按“任意匹配”处理。
 */
export class FilterRarityNode extends RuntimeNodeBase {
    /**
     * 输出目标数组端口。
     *
     * @returns 品质匹配的目标集合。
     */
    public targets(): Array<RuntimePropInstance | PropRuntimeGraph> {
        const targets = this.inputTargetValues('targets');
        const rarityValues = this.inputValues('rarity')
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value));
        if (!rarityValues.length) return targets;
        const set = new Set(rarityValues);
        return targets.filter((item) => set.has(Number(readTargetGraph(item).rarity)));
    }
}

/**
 * 标签筛选节点。
 *
 * 从上游目标中筛出带有指定标签的实例；多个标签输入按“任意匹配”处理。
 */
export class FilterTagNode extends RuntimeNodeBase {
    /**
     * 输出目标数组端口。
     *
     * @returns 标签匹配的目标集合。
     */
    public targets(): Array<RuntimePropInstance | PropRuntimeGraph> {
        const targets = this.inputTargetValues('targets');
        const tagValues = this.inputValues('tag')
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value));
        if (!tagValues.length) return targets;
        const set = new Set(tagValues);
        return targets.filter((item) => readTargetGraph(item).tags.some((tag) => set.has(Number(tag))));
    }
}

/**
 * 原型筛选节点。
 *
 * 比较的是“实例当前原型 `_id`”和筛选原型 `_id`，不是实例 id。
 */
export class FilterPrototypeNode extends RuntimeNodeBase {
    /**
     * 输出目标数组端口。
     *
     * @returns 当前原型匹配的目标集合。
     */
    public targets(): Array<RuntimePropInstance | PropRuntimeGraph> {
        const targets = this.inputTargetValues('targets');
        const prototypeIds = new Set(this.inputPrototypeIds('prototype'));
        if (!prototypeIds.size) return targets;
        return targets.filter((item) => prototypeIds.has(readTargetGraph(item)._id));
    }
}

/**
 * 随机筛选节点。
 *
 * 从上游目标中随机抽取指定数量；数量端口未连接时默认抽取 1 个。
 */
export class FilterRandomNode extends RuntimeNodeBase {
    /**
     * 输出目标数组端口。
     *
     * @returns 随机抽取后的目标集合。
     */
    public targets(): Array<RuntimePropInstance | PropRuntimeGraph> {
        const targets = this.inputTargetValues('targets');
        const count = this.inputNumber('count', 1);
        return pickRandomItems(targets, count, this.context.rng);
    }
}

function readTargetGraph(target: RuntimePropInstance | PropRuntimeGraph): PropRuntimeGraph {
    return isRuntimePropInstance(target) ? target.graph : target;
}
